import htmlCssBackground from '../img/imagenes-cursos/htmlcss-background.webp';


export interface CourseDate {
  id: string;
  editionName: string;
  startDate: string;
  endDate: string;
  totalSpots: number;
  availableSpots: number;
  status: 'Abierto' | 'Próximamente' | 'Completo';
  classDays?: string;
  classSchedule?: string;
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface SyllabusModule {
  title: string;
  duration: string;
  lessons: string[];
}

export interface Certificate {
  title: string;
  description: string;
  features: string[];
}

export interface Course {
  id: string;
  title: string;
  description: string;
  detailedDescription: string;
  image: string;
  price: number;
  discountPrice: number;
  installments: number;
  category: string;
  badge?: string;
  duration: string;
  level: string;
  students: number;
  rating: number;
  active: boolean;
  type: 'curso' | 'carrera';
  content: string[];
  benefits: string[];
  requirements: string[];
  faq: FAQ[];
  syllabus: SyllabusModule[];
  certificate: Certificate;
  whatsappNumber?: string;
  dates?: CourseDate[];
  syllabusPdfUrl?: string;
}

// Curso HTML5 & CSS3
const course: Course = {
  id: 'html5-css3',
  title: 'HTML 5 y CSS 3',
  description: 'Aprende a dominar los lenguajes más utilizados en el desarrollo web.',
  image: 'https://argoadigital.com.ar/img/cursos/certificado-html.webp',
  price: 150000,
  discountPrice: 120000,
  installments: 6,
  category: 'Desarrollo Web',
  badge: 'El número 1 en inscripciones',
  duration: '8 semanas',
  level: 'Principiante',
  students: 2400,
  rating: 4.9,
  active: true,
  type: 'curso',
  detailedDescription:
    'Domina los fundamentos del desarrollo web con HTML5 y CSS3. Aprenderás a crear páginas web modernas, semánticas y bien estructuradas. Desde las etiquetas básicas hasta técnicas avanzadas de maquetado y estilos, este curso te dará la base sólida que necesitás para convertirte en desarrollador web.',
  content: [
    'Introducción a HTML5 y estructura semántica',
    'Etiquetas de texto, listas y tablas',
    'Formularios y validaciones HTML5',
    'Introducción a CSS3 y selectores',
    'Box Model, Flexbox y Grid',
    'Tipografías, colores y variables CSS',
    'Animaciones y transiciones CSS',
    'Proyecto final: sitio web completo',
  ],
  benefits: [
    'Certificado oficial al finalizar',
    'Acceso de por vida al contenido',
    'Clases en vivo semanales',
    'Grupo privado de estudiantes',
    'Soporte personalizado',
    'Proyectos prácticos reales',
    'Actualizaciones gratuitas del curso',
  ],
  requirements: [
    'No se requiere experiencia previa en programación',
    'Computadora con acceso a internet',
    'Ganas de aprender y practicar',
    'Editor de texto (te recomendamos VS Code, es gratuito)',
    'Navegador web actualizado (Chrome o Firefox)',
  ],
  faq: [
    {
      question: '¿Necesito saber programar para inscribirme?',
      answer: 'No, este curso está diseñado para principiantes absolutos. Empezamos desde cero y avanzamos de forma gradual.',
    },
    {
      question: '¿Las clases son grabadas o en vivo?',
      answer: 'Las clases son en vivo con el docente. Además, quedan grabadas para que puedas repasar cuando quieras.',
    },
    {
      question: '¿Cuántas horas semanales debo dedicar?',
      answer: 'Recomendamos entre 6 y 8 horas semanales entre clases y práctica para aprovechar al máximo el curso.',
    },
    {
      question: '¿Obtengo certificado al finalizar?',
      answer: 'Sí, al completar el curso y el proyecto final recibís un certificado oficial de Argoa Digital con validez comprobable.',
    },
    {
      question: '¿Puedo pagar en cuotas?',
      answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con tarjeta de crédito.',
    },
    {
      question: '¿Qué pasa si me pierdo una clase?',
      answer: 'Todas las clases quedan grabadas y disponibles en tu área de alumnos. No te perdés nada.',
    },
  ],
  syllabus: [
    {
      title: 'Módulo 1: Fundamentos de HTML5',
      duration: '1 semana',
      lessons: [
        '¿Qué es HTML y cómo funciona?',
        'Estructura básica de un documento HTML',
        'Etiquetas semánticas: header, nav, main, footer',
        'Textos, párrafos, encabezados y listas',
        'Links, imágenes y multimedia',
      ],
    },
    {
      title: 'Módulo 2: Formularios y Tablas',
      duration: '1 semana',
      lessons: [
        'Creación de formularios HTML5',
        'Tipos de inputs y validaciones nativas',
        'Tablas y datos tabulares',
        'Atributos de accesibilidad',
      ],
    },
    {
      title: 'Módulo 3: Introducción a CSS3',
      duration: '1 semana',
      lessons: [
        '¿Qué es CSS y cómo se aplica?',
        'Selectores básicos y avanzados',
        'Colores, fuentes y tipografías',
        'Box Model: margin, padding, border',
      ],
    },
    {
      title: 'Módulo 4: Layout con Flexbox',
      duration: '2 semanas',
      lessons: [
        'Concepto de Flexbox',
        'Propiedades del contenedor flex',
        'Propiedades de los ítems flex',
        'Layouts reales con Flexbox',
        'Ejercicios prácticos',
      ],
    },
    {
      title: 'Módulo 5: CSS Grid',
      duration: '1 semana',
      lessons: [
        'Concepto de CSS Grid',
        'Filas, columnas y áreas',
        'Grid vs Flexbox: cuándo usar cada uno',
        'Layouts complejos con Grid',
      ],
    },
    {
      title: 'Módulo 6: Animaciones y Variables CSS',
      duration: '1 semana',
      lessons: [
        'Variables CSS (custom properties)',
        'Transiciones y efectos hover',
        'Animaciones con @keyframes',
        'Efectos modernos de UI',
      ],
    },
    {
      title: 'Módulo 7: Proyecto Final',
      duration: '1 semana',
      lessons: [
        'Planificación del proyecto',
        'Maquetado completo de un sitio web',
        'Revisión y feedback del docente',
        'Entrega y certificación',
      ],
    },
  ],
  certificate: {
    title: 'Certificado en HTML5 y CSS3',
    description: 'Al finalizar el curso y aprobar el proyecto final, recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en HTML5 y CSS3.',
    features: [
      'Certificado digital con código QR verificable',
      'Emitido por Argoa Digital',
      'Compartible en LinkedIn y redes sociales',
      'Válido para tu portfolio profesional',
      'Descargable en PDF de alta calidad',
    ],
  },
  whatsappNumber: '5491100000000',
  dates: [
    {
      id: 'html-2026-03',
      editionName: 'Edición Marzo 2026',
      startDate: '2026-03-10',
      endDate: '2026-05-05',
      totalSpots: 30,
      availableSpots: 8,
      status: 'Abierto',
      classDays: 'Lunes y Miércoles',
      classSchedule: '19:00 - 21:00 hs',
    },
    {
      id: 'html-2026-04',
      editionName: 'Edición Abril 2026',
      startDate: '2026-04-14',
      endDate: '2026-06-09',
      totalSpots: 30,
      availableSpots: 22,
      status: 'Próximamente',
      classDays: 'Martes y Jueves',
      classSchedule: '18:00 - 20:00 hs',
    },
  ],
};

export default course;

export const courses: Course[] = [
  course,
  {
    id: 'responsive-web-design',
    title: 'Responsive Web Design',
    description: 'Creá sitios web adaptables a cualquier dispositivo con Bootstrap, el framework más popular del mercado.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-responsive.webp',
    price: 95189,
    discountPrice: 95189,
    installments: 6,
    category: 'Desarrollo Web',
    badge: 'Nivel Intermedio',
    duration: '2 meses / 15 horas',
    level: 'Intermedio',
    students: 1800,
    rating: 4.8,
    active: true,
    type: 'curso',
    detailedDescription:
      'Ya dominás HTML y CSS, ahora es momento de crear sitios web adaptables a cualquier dispositivo. Con esta técnica, tus proyectos no solo serán más competitivos, sino que destacarán en un mercado en constante evolución y alta demanda. Aprendé Bootstrap, el framework más popular y requerido del mercado, para optimizar tus webs al máximo y lograr resultados profesionales.',
    content: [
      'Conceptos del diseño adaptable o responsive',
      'Etiqueta "meta" y el concepto viewport',
      'Rompiendo paradigmas con Mobile First',
      'Utilizando las Media Queries',
      'Imágenes y videos adaptables',
      'Nuevos valores de medición, el REM y EM',
      'Bootstrap: grid system y componentes',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Conocimientos de HTML 5 y CSS 3',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Editor de texto (Visual Studio Code)',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema, y si es necesario podrás cursar nuevamente los temas donde hayas tenido inconvenientes.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases de los cursos en los que te hayas capacitado, durante o al finalizarlos, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos Responsive',
        duration: '1 semana',
        lessons: ['Concepto de diseño adaptable', 'Viewport y meta tags', 'Mobile First', 'Media Queries'],
      },
      {
        title: 'Módulo 2: Bootstrap',
        duration: '2 semanas',
        lessons: ['Grid system de Bootstrap', 'Componentes y utilidades', 'Navbar y formularios', 'Personalización'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Planificación del proyecto', 'Desarrollo responsive completo', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Responsive Web Design',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en diseño web responsive y Bootstrap.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [
      {
        id: 'rwd-2026-03',
        editionName: 'Edición Marzo 2026',
        startDate: '2026-03-21',
        endDate: '2026-04-18',
        totalSpots: 30,
        availableSpots: 15,
        status: 'Abierto',
      },
      {
        id: 'rwd-2026-04',
        editionName: 'Edición Abril 2026',
        startDate: '2026-04-14',
        endDate: '2026-05-12',
        totalSpots: 30,
        availableSpots: 25,
        status: 'Próximamente',
      },
    ],
  },
  {
    id: 'desarrollo-web-profesional',
    title: 'Desarrollo Web Profesional',
    description: 'Curso completo y avanzado: HTML5, CSS3, Responsive, Bootstrap y fundamentos de JavaScript.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-dwp.webp',
    price: 239853,
    discountPrice: 239853,
    installments: 6,
    category: 'Desarrollo Web',
    badge: 'El más solicitado',
    duration: '3 meses / 84 horas',
    level: 'Inicial a Avanzado',
    students: 3200,
    rating: 4.9,
    active: true,
    type: 'curso',
    detailedDescription:
      'En este completo curso aprenderás desde cero los lenguajes y técnicas más utilizados por desarrolladores para crear sitios web modernos y funcionales. Exploraremos juntos: HTML5, CSS3, CSS3 Avanzado, Responsive Web Design, Bootstrap, y fundamentos de JavaScript. Al finalizar, tendrás todas las herramientas necesarias para comenzar tu carrera como Desarrollador Web Profesional.',
    content: [
      'Fundamentos de HTML',
      'Definición de etiquetas',
      'Estructura básica',
      'Vínculos y tipografías',
      'Float - Flex Box - CSS Grid',
      'Responsive Web Design y Bootstrap',
      'Fundamentos de JavaScript',
      'Proyecto integrador final',
    ],
    benefits: [
      'El más solicitado por las empresas',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Editor de texto (Ejemplo, Visual Studio Code)',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema, y si es necesario podrás cursar nuevamente los temas donde hayas tenido inconvenientes.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: HTML5 Completo',
        duration: '3 semanas',
        lessons: ['Fundamentos de HTML', 'Etiquetas semánticas', 'Formularios y tablas', 'Vínculos y multimedia'],
      },
      {
        title: 'Módulo 2: CSS3 y Layouts',
        duration: '4 semanas',
        lessons: ['CSS3 básico y avanzado', 'Float, Flexbox y Grid', 'Animaciones y transiciones', 'Variables CSS'],
      },
      {
        title: 'Módulo 3: Responsive y Bootstrap',
        duration: '3 semanas',
        lessons: ['Mobile First', 'Media Queries', 'Bootstrap grid y componentes', 'Proyectos responsive'],
      },
      {
        title: 'Módulo 4: JavaScript y Proyecto Final',
        duration: '2 semanas',
        lessons: ['Fundamentos de JavaScript', 'DOM y eventos', 'Proyecto integrador', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Desarrollo Web Profesional',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tu formación completa como Desarrollador Web Profesional.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [
      {
        id: 'dwp-2026-03',
        editionName: 'Edición Marzo 2026',
        startDate: '2026-03-16',
        endDate: '2026-07-06',
        totalSpots: 25,
        availableSpots: 10,
        status: 'Abierto',
      },
      {
        id: 'dwp-2026-04',
        editionName: 'Edición Abril 2026',
        startDate: '2026-04-07',
        endDate: '2026-07-14',
        totalSpots: 25,
        availableSpots: 20,
        status: 'Próximamente',
      },
    ],
  },
  {
    id: 'desarrollo-ux-ui',
    title: 'Desarrollo y Diseño UX-UI',
    description: 'Creá experiencias digitales intuitivas y atractivas combinando diseño y funcionalidad.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-uxui.webp',
    price: 185792,
    discountPrice: 185792,
    installments: 6,
    category: 'Diseño',
    badge: 'Alta demanda',
    duration: '3 meses / 42 horas',
    level: 'Inicial a Avanzado',
    students: 1400,
    rating: 4.8,
    active: true,
    type: 'curso',
    detailedDescription:
      'En este curso de UX-UI aprenderás a crear experiencias digitales intuitivas y atractivas. Descubrirás cómo combinar diseño y funcionalidad para que los usuarios disfruten cada interacción. Desde los principios de usabilidad hasta herramientas clave, desarrollarás habilidades esenciales para destacarte en el mundo del diseño UX/UI.',
    content: [
      '¿Qué es UX? Definición y objetivos',
      'Buyer persona y Proto-persona',
      'Arquitectura de la información',
      '¿Qué es UI y en qué se diferencia de UX?',
      'User Story, Task Flow y User Flow',
      'Regla de 8/4',
      'Prototipado y wireframes',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Manejo básico de la PC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de UX',
        duration: '3 semanas',
        lessons: ['¿Qué es UX?', 'Buyer persona', 'Arquitectura de la información', 'User Story y User Flow'],
      },
      {
        title: 'Módulo 2: Diseño UI',
        duration: '3 semanas',
        lessons: ['¿Qué es UI?', 'Regla de 8/4', 'Sistemas de diseño', 'Componentes visuales'],
      },
      {
        title: 'Módulo 3: Herramientas y Proyecto',
        duration: '3 semanas',
        lessons: ['Figma y herramientas de prototipado', 'Wireframes y mockups', 'Proyecto integrador', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Desarrollo y Diseño UX-UI',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en diseño UX/UI.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'ia-aplicada-al-desarrollo-web',
    title: 'La IA Aplicada al Desarrollo Web',
    description: 'Aprendé a utilizar herramientas de IA para automatizar procesos, optimizar código y mejorar la experiencia del usuario.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-ia-desarrollo-web.webp',
    price: 152750,
    discountPrice: 152750,
    installments: 6,
    category: 'Desarrollo Web',
    badge: 'Tendencia 2025',
    duration: '24 horas',
    level: 'Inicial a Avanzado',
    students: 950,
    rating: 4.9,
    active: true,
    type: 'curso',
    detailedDescription:
      'Aprendé a utilizar herramientas de IA para automatizar procesos, optimizar código y mejorar la experiencia del usuario. Explorá cómo puede ayudarte en diseño UX/UI, generación de contenido, análisis de datos, SEO y desarrollo front-end y back-end. Domina las tecnologías que están revolucionando la industria digital.',
    content: [
      'Conceptos de inteligencia artificial',
      'ChatGPT, Gemini, Llama y Copilot',
      'Experiencia del Usuario (UX) con IA',
      'Los prompts y las imágenes',
      'La IA, HTML y CSS',
      'El SEO con IA',
      'IA en front-end y back-end',
      'Proyecto integrador final',
    ],
    benefits: [
      'Solicitado por empresas',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Editor de texto (Ejemplo, Visual Studio Code)',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de IA',
        duration: '2 semanas',
        lessons: ['Conceptos de inteligencia artificial', 'ChatGPT, Gemini, Llama y Copilot', 'Prompts efectivos', 'IA para imágenes'],
      },
      {
        title: 'Módulo 2: IA en Diseño y Desarrollo',
        duration: '2 semanas',
        lessons: ['UX con IA', 'HTML y CSS asistido por IA', 'SEO con IA', 'Front-end y back-end con IA'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador con IA', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en La IA Aplicada al Desarrollo Web',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en IA aplicada al desarrollo web.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'photoshop',
    title: 'Adobe Photoshop CC - Full Level',
    description: 'Potenciá tus proyectos dominando Photoshop desde cero hasta efectos y técnicas profesionales.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-photoshop.webp',
    price: 195250,
    discountPrice: 195250,
    installments: 6,
    category: 'Diseño',
    badge: '98% de valoración',
    duration: '2 meses / 33 horas',
    level: 'Inicial a Avanzado',
    students: 2100,
    rating: 4.9,
    active: true,
    type: 'curso',
    detailedDescription:
      'Potenciá tus proyectos con este Curso de Photoshop. Llevá tus ideas a un nivel profesional y dominá esta poderosa herramienta. Aprendé a realizar retoques y montajes fotográficos de manera fácil y efectiva. Empezamos desde cero, avanzando hasta efectos y técnicas profesionales. Photoshop es el software más utilizado para publicaciones en Redes Sociales.',
    content: [
      'Entorno e interface del programa',
      'Principales herramientas',
      'Tipos de imágenes y sus usos',
      'Colores, curvas y brillo',
      'El uso de las capas y canales',
      'Modo de fusión y fotomontaje',
      'Retoque fotográfico profesional',
      'Proyecto integrador final',
    ],
    benefits: [
      '98% de valoración',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe Photoshop CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de Photoshop',
        duration: '2 semanas',
        lessons: ['Entorno e interface', 'Herramientas principales', 'Tipos de imágenes', 'Colores y ajustes'],
      },
      {
        title: 'Módulo 2: Técnicas Avanzadas',
        duration: '3 semanas',
        lessons: ['Capas y canales', 'Modos de fusión', 'Fotomontaje', 'Retoque profesional'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe Photoshop CC - Full Level',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en Adobe Photoshop CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'illustrator',
    title: 'Adobe Illustrator CC',
    description: 'Aprendé ilustración digital con el software Nº 1 en diseño vectorial: Adobe Illustrator CC.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-illustrator.webp',
    price: 175250,
    discountPrice: 175250,
    installments: 6,
    category: 'Diseño',
    badge: 'Nivel Inicial',
    duration: '1 mes / 18 horas',
    level: 'Inicial',
    students: 1600,
    rating: 4.8,
    active: true,
    type: 'curso',
    detailedDescription:
      'Aprendé ilustración digital con el software Nº 1 en diseño vectorial: Adobe Illustrator CC. Domina la creación de piezas gráficas, como posters, folletos, avisos publicitarios y contenido para redes sociales. Comenzá desde cero y explorá herramientas esenciales para diseñar con precisión y creatividad.',
    content: [
      'Presentación de Adobe Illustrator',
      'Presentación de los principales paneles',
      'Diferentes Modos de color',
      'Máscaras',
      'Trabajar con Pinceles',
      'Herramienta Malla',
      'Creación de logos y piezas gráficas',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe Illustrator CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de Illustrator',
        duration: '1 semana',
        lessons: ['Presentación del programa', 'Paneles principales', 'Modos de color', 'Herramientas básicas'],
      },
      {
        title: 'Módulo 2: Técnicas Avanzadas',
        duration: '2 semanas',
        lessons: ['Máscaras', 'Pinceles', 'Herramienta Malla', 'Creación de logos'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe Illustrator CC',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en Adobe Illustrator CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'indesign',
    title: 'Adobe InDesign CC',
    description: 'Potenciá tu carrera en el diseño editorial con el software más solicitado por las empresas del sector.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-indesing.webp',
    price: 175250,
    discountPrice: 175250,
    installments: 6,
    category: 'Diseño',
    badge: 'Diseño Editorial',
    duration: '18 horas',
    level: 'Inicial a Avanzado',
    students: 1100,
    rating: 4.7,
    active: true,
    type: 'curso',
    detailedDescription:
      'Potenciá tu carrera en el diseño editorial con el software más solicitado por las empresas del sector. Adobe InDesign CC es una de las herramientas más completas y competitivas para diseño editorial, piezas gráficas, contenidos multimedia y diseño web. Conocé los principales tips y reglas del diseño editorial y gráfico para crear publicaciones profesionales con alto impacto visual.',
    content: [
      'Entorno e interface del programa',
      'Principales herramientas',
      'Trabajando con tipografías',
      'Interlineado, kerning y tracking',
      'Las imágenes y sus usos',
      'Dibujo vectorial',
      'Diseño editorial profesional',
      'Proyecto integrador final',
    ],
    benefits: [
      'El más solicitado por editoriales',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe InDesign CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de InDesign',
        duration: '1 semana',
        lessons: ['Entorno e interface', 'Herramientas principales', 'Tipografías', 'Interlineado y kerning'],
      },
      {
        title: 'Módulo 2: Diseño Editorial',
        duration: '2 semanas',
        lessons: ['Imágenes y sus usos', 'Dibujo vectorial', 'Maquetación profesional', 'Publicaciones impresas y digitales'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe InDesign CC',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en Adobe InDesign CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'premiere-pro',
    title: 'Adobe Premiere Pro CC',
    description: 'Convertite en experto en edición de video con el software líder en la industria audiovisual.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-premier.webp',
    price: 132500,
    discountPrice: 132500,
    installments: 6,
    category: 'Audiovisual',
    badge: 'Edición de Video',
    duration: '7 semanas / 24 horas',
    level: 'Inicial a Avanzado',
    students: 1300,
    rating: 4.8,
    active: true,
    type: 'curso',
    detailedDescription:
      'Convertite en un experto en edición de video con el software líder en la industria: Adobe Premiere Pro CC. Ideal para creadores de contenido, cineastas y profesionales del marketing digital. Aprendé desde cero a editar videos de manera profesional, aplicando transiciones, efectos, corrección de color y técnicas avanzadas para lograr producciones de alto impacto.',
    content: [
      'Configuración y Herramientas',
      'Pistas de Audio y Video',
      'Keyframe',
      'Materiales vía cámara',
      'Formas Básicas',
      'Render',
      'Corrección de color',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe Premiere Pro CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de Premiere',
        duration: '2 semanas',
        lessons: ['Configuración y herramientas', 'Pistas de audio y video', 'Keyframe', 'Materiales vía cámara'],
      },
      {
        title: 'Módulo 2: Edición Avanzada',
        duration: '3 semanas',
        lessons: ['Formas básicas', 'Corrección de color', 'Transiciones y efectos', 'Render y exportación'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '2 semanas',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe Premiere Pro CC',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en Adobe Premiere Pro CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'after-effects',
    title: 'Adobe After Effects CC - Inicial',
    description: 'Aprendé animación y efectos visuales con el software más potente de la industria audiovisual.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-after-effects.webp',
    price: 132500,
    discountPrice: 132500,
    installments: 6,
    category: 'Audiovisual',
    badge: 'Motion Graphics',
    duration: '2 meses / 18 horas',
    level: 'Inicial',
    students: 1050,
    rating: 4.8,
    active: true,
    type: 'curso',
    detailedDescription:
      'Aprendé animación y efectos visuales con el software más potente de la industria: Adobe After Effects CC. Ideal para creadores de contenido, diseñadores y editores de video que buscan llevar sus producciones al siguiente nivel. Creá animaciones, motion graphics y efectos de alto impacto.',
    content: [
      'Interfaz del usuario',
      'Preparación y proyecto',
      'Importar material AI y PS',
      'Modos de fusión',
      'Luces y cámaras',
      'Rastreando movimientos',
      'Animaciones y motion graphics',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Sistema Operativo: Windows 10 / Linux / Mac 10.15',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe After Effects CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Fundamentos de After Effects',
        duration: '2 semanas',
        lessons: ['Interfaz del usuario', 'Preparación y proyecto', 'Importar material AI y PS', 'Modos de fusión'],
      },
      {
        title: 'Módulo 2: Animación y Efectos',
        duration: '3 semanas',
        lessons: ['Luces y cámaras', 'Rastreo de movimientos', 'Motion graphics', 'Efectos visuales'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe After Effects CC - Inicial',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos en Adobe After Effects CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
  {
    id: 'after-effects-avanzado',
    title: 'Adobe After Effects CC - Avanzado',
    description: 'Explorá técnicas avanzadas: expresiones, tracking, partículas, 3D y composición multicapa.',
    image: 'https://argoadigital.com.ar/img/cursos/certificado-after-effects-avanzado.webp',
    price: 116500,
    discountPrice: 116500,
    installments: 6,
    category: 'Audiovisual',
    badge: 'Nivel Avanzado',
    duration: '1 mes / 12 horas',
    level: 'Avanzado',
    students: 720,
    rating: 4.9,
    active: true,
    type: 'curso',
    detailedDescription:
      'Explorá técnicas avanzadas como expresiones, tracking, partículas, 3D y composición multicapa. Aprendé a optimizar flujos de trabajo y a integrar After Effects con Premiere Pro, Photoshop e Illustrator. Convertite en un experto en postproducción y creá efectos visuales de nivel cinematográfico para publicidad, redes sociales y proyectos audiovisuales.',
    content: [
      'Recortes de croma',
      'Iluminación, color y partículas',
      'Expressions',
      'Expresiones y el texto',
      'Cámaras, luces y sombras',
      'El uso del rigging',
      'Integración con Premiere, Photoshop e Illustrator',
      'Proyecto integrador final',
    ],
    benefits: [
      'Rápida salida laboral',
      'Precio realmente competitivo',
      'Trabajo integrador final',
      'Todas las clases se graban',
      'Instructores personalizados y líderes en el sector',
      'Certificado de asistencia al curso',
    ],
    requirements: [
      'Haber realizado el Curso Inicial de After Effects',
      'PC recomendada: AMD Phenom III - I3 o superior',
      'Memoria RAM: 4GB (Recomendado)',
      'Software Adobe After Effects CC',
      'Auriculares con micrófono',
      'WebCam (No obligatorio)',
    ],
    faq: [
      {
        question: '¿Por qué aprender con cursos Online en vivo?',
        answer: 'Porque el hecho de hacerlo en vivo y con instructores capacitados, nos ayudará a evacuar todas las dudas en ese mismo instante y seguir aprendiendo a nuestro ritmo.',
      },
      {
        question: '¿Al finalizar el curso, cómo sé si realmente aprobé?',
        answer: 'Al finalizar tu cursada, vas a tener un exámen para evaluar si realmente aprendiste sobre el tema.',
      },
      {
        question: '¿Las clases grabadas las puedo descargar?',
        answer: 'En Argoa Digital vas a descargar sin inconvenientes todas las clases, tenés 15 días para hacerlo luego de la última fecha de la clase.',
      },
      {
        question: '¿Puedo pagar en cuotas?',
        answer: 'Sí, podés pagar en hasta 6 cuotas sin interés con Mercado Pago.',
      },
    ],
    syllabus: [
      {
        title: 'Módulo 1: Técnicas Avanzadas',
        duration: '2 semanas',
        lessons: ['Recortes de croma', 'Iluminación y partículas', 'Expressions', 'Expresiones y texto'],
      },
      {
        title: 'Módulo 2: 3D y Composición',
        duration: '1 semana',
        lessons: ['Cámaras, luces y sombras', 'Rigging', 'Integración con otras apps Adobe', 'Flujos de trabajo avanzados'],
      },
      {
        title: 'Módulo 3: Proyecto Final',
        duration: '1 semana',
        lessons: ['Proyecto integrador', 'Revisión y feedback', 'Entrega y certificación'],
      },
    ],
    certificate: {
      title: 'Certificado en Adobe After Effects CC - Avanzado',
      description: 'Al finalizar el curso recibís tu certificado oficial de Argoa Digital que acredita tus conocimientos avanzados en Adobe After Effects CC.',
      features: [
        'Certificado digital verificable',
        'Emitido por Argoa Digital',
        'Compartible en LinkedIn y redes sociales',
        'Válido para tu portfolio profesional',
      ],
    },
    whatsappNumber: '5491100000000',
    dates: [],
  },
];
